import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import {
  useDeletePublicationProfileOverride,
  useEffectivePublicationProfile,
  usePublicationCatalogTypes,
  usePutPublicationProfileOverride,
} from '../../hooks';
import { formatDateTime } from '../../lib/format';
import { useToast } from '../../lib/useToast';
import {
  PUBLICATION_PROFILE_FIELD_LABELS,
  PUBLICATION_PROFILE_SOURCE_LABELS,
  publicationProfileSourceColor,
} from '../../model/options';
import type { PublicationProfileFieldKind, PublicationStockScope } from '../../model/publicationTypes';
import { PublicationProfileOverrideDialog } from './PublicationProfileOverrideDialog';

/** Catalog types (read-only registry) + per-type publication profile viewer/override editor. */
export function PublicationCatalogTypesPanel({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('catalog');
  const typesQuery = usePublicationCatalogTypes();
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const profileQuery = useEffectivePublicationProfile(selectedCode ?? undefined, {
    enabled: selectedCode !== null,
  });
  const putOverride = usePutPublicationProfileOverride();
  const deleteOverride = useDeletePublicationProfileOverride();
  const { toast, success, error: notifyError, clear } = useToast();

  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  if (typesQuery.isLoading) {
    return <LoadingState label={t('publicationCatalogTypesPanel.loading')} />;
  }
  if (typesQuery.isError) {
    return <ErrorState message={typesQuery.error?.message} onRetry={() => void typesQuery.refetch()} />;
  }

  const types = typesQuery.data ?? [];
  const selectedType = types.find((ct) => ct.code === selectedCode) ?? null;

  const handleError = (error: NormalizedApiError) => notifyError(error.message);

  const submitOverride = (value: {
    fields: PublicationProfileFieldKind[];
    minPhotoCount: number | null;
    requireActivePrice: boolean;
    requireStock: boolean;
    minAvailableQty: number | null;
    stockScope: PublicationStockScope;
    stockLocationId: string | null;
    allowCheckAvailability: boolean;
    allowBackorder: boolean;
  }) => {
    if (!selectedCode) return;
    putOverride.mutate(
      {
        code: selectedCode,
        body: {
          fields: value.fields,
          minPhotoCount: value.minPhotoCount ?? undefined,
          requireActivePrice: value.requireActivePrice,
          requireStock: value.requireStock,
          minAvailableQty: value.minAvailableQty ?? undefined,
          stockScope: value.requireStock ? value.stockScope : undefined,
          stockLocationId: value.stockLocationId ?? undefined,
          allowCheckAvailability: value.allowCheckAvailability,
          allowBackorder: value.allowBackorder,
        },
      },
      {
        onSuccess: () => {
          success(t('publicationCatalogTypesPanel.overrideSaved'));
          setOverrideDialogOpen(false);
        },
        onError: handleError,
      },
    );
  };

  const submitReset = () => {
    if (!selectedCode) return;
    deleteOverride.mutate(selectedCode, {
      onSuccess: () => {
        success(t('publicationCatalogTypesPanel.overrideReset'));
        setResetConfirmOpen(false);
      },
      onError: (error) => {
        handleError(error);
        setResetConfirmOpen(false);
      },
    });
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('publicationCatalogTypesPanel.description')}
      </Typography>

      {types.length === 0 ? (
        <EmptyState
          title={t('publicationCatalogTypesPanel.empty.title')}
          description={t('publicationCatalogTypesPanel.empty.description')}
        />
      ) : (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: selectedType ? 7 : 12 }}>
            <Card variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('publicationCatalogTypesPanel.table.code')}</TableCell>
                    <TableCell>{t('publicationCatalogTypesPanel.table.displayName')}</TableCell>
                    <TableCell>{t('publicationCatalogTypesPanel.table.topics')}</TableCell>
                    <TableCell>{t('publicationCatalogTypesPanel.table.active')}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {types.map((ct) => (
                    <TableRow
                      key={ct.code}
                      hover
                      selected={ct.code === selectedCode}
                      onClick={() => setSelectedCode(ct.code)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography component="code" variant="body2">
                          {ct.code}
                        </Typography>
                      </TableCell>
                      <TableCell>{ct.displayName}</TableCell>
                      <TableCell>
                        {ct.productTopic ? (
                          <Typography variant="caption" component="code" sx={{ display: 'block' }} noWrap>
                            {ct.productTopic}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={ct.active ? 'success' : 'default'}
                          variant={ct.active ? 'filled' : 'outlined'}
                          label={
                            ct.active
                              ? t('publicationCatalogTypesPanel.table.activeYes')
                              : t('publicationCatalogTypesPanel.table.activeNo')
                          }
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
                        <Tooltip title={t('publicationCatalogTypesPanel.table.viewProfile')}>
                          <IconButton
                            size="small"
                            aria-label={t('publicationCatalogTypesPanel.table.viewProfileAria', {
                              code: ct.code,
                            })}
                            onClick={() => setSelectedCode(ct.code)}
                          >
                            <VisibilityRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </Grid>

          {selectedType ? (
            <Grid size={{ xs: 12, md: 5 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {t('publicationCatalogTypesPanel.profileCard.title', { name: selectedType.displayName })}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {profileQuery.isLoading ? (
                    <LoadingState label={t('publicationCatalogTypesPanel.profileCard.loading')} />
                  ) : profileQuery.isError ? (
                    <ErrorState
                      message={profileQuery.error?.message}
                      onRetry={() => void profileQuery.refetch()}
                    />
                  ) : profileQuery.data ? (
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip
                          size="small"
                          color={publicationProfileSourceColor(profileQuery.data.source)}
                          variant={profileQuery.data.source === 'TENANT_OVERRIDE' ? 'filled' : 'outlined'}
                          label={t(PUBLICATION_PROFILE_SOURCE_LABELS[profileQuery.data.source])}
                        />
                      </Stack>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('publicationCatalogTypesPanel.profileCard.requiredFieldsLabel')}
                        </Typography>
                        {profileQuery.data.fields.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('publicationCatalogTypesPanel.profileCard.noRequiredFields')}
                          </Typography>
                        ) : (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {profileQuery.data.fields.map((f) => (
                              <Chip key={f} size="small" variant="outlined" label={t(PUBLICATION_PROFILE_FIELD_LABELS[f])} />
                            ))}
                          </Stack>
                        )}
                      </Box>
                      <Typography variant="body2">
                        {t('publicationCatalogTypesPanel.profileCard.minPhotoCount', {
                          count: profileQuery.data.minPhotoCount ?? 0,
                        })}
                      </Typography>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('publicationCatalogTypesPanel.profileCard.productChecksLabel')}
                        </Typography>
                        {profileQuery.data.requireActivePrice || profileQuery.data.requireStock ? (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {profileQuery.data.requireActivePrice ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t('publicationCatalogTypesPanel.profileCard.requireActivePrice')}
                              />
                            ) : null}
                            {profileQuery.data.requireStock ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t('publicationCatalogTypesPanel.profileCard.requireStockWithPolicy', {
                                  quantity: profileQuery.data.minAvailableQty ?? 1,
                                  scope: profileQuery.data.stockScope ?? 'AGGREGATE',
                                })}
                              />
                            ) : null}
                            {profileQuery.data.requireStock && profileQuery.data.stockLocationId ? (
                              <Chip size="small" variant="outlined" label={profileQuery.data.stockLocationId} />
                            ) : null}
                            {profileQuery.data.allowCheckAvailability ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t('publicationCatalogTypesPanel.profileCard.allowCheckAvailability')}
                              />
                            ) : null}
                            {profileQuery.data.allowBackorder ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t('publicationCatalogTypesPanel.profileCard.allowBackorder')}
                              />
                            ) : null}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('publicationCatalogTypesPanel.profileCard.noProductChecks')}
                          </Typography>
                        )}
                      </Box>
                      {canManage ? (
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditRoundedIcon />}
                            onClick={() => setOverrideDialogOpen(true)}
                          >
                            {t('publicationCatalogTypesPanel.profileCard.editOverride')}
                          </Button>
                          {profileQuery.data.source === 'TENANT_OVERRIDE' ? (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<RestoreRoundedIcon />}
                              onClick={() => setResetConfirmOpen(true)}
                            >
                              {t('publicationCatalogTypesPanel.profileCard.resetToDefault')}
                            </Button>
                          ) : null}
                        </Stack>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        {t('publicationCatalogTypesPanel.profileCard.updated', {
                          date: formatDateTime(selectedType.updatedAt),
                        })}
                      </Typography>
                    </Stack>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          ) : null}
        </Grid>
      )}

      <PublicationProfileOverrideDialog
        open={overrideDialogOpen}
        catalogTypeCode={selectedCode ?? ''}
        current={profileQuery.data}
        saving={putOverride.isPending}
        onClose={() => setOverrideDialogOpen(false)}
        onSubmit={submitOverride}
      />

      <Dialog open={resetConfirmOpen} onClose={() => (!deleteOverride.isPending ? setResetConfirmOpen(false) : undefined)}>
        <DialogTitle>{t('publicationCatalogTypesPanel.resetDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('publicationCatalogTypesPanel.resetDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)} disabled={deleteOverride.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={submitReset}
            disabled={deleteOverride.isPending}
            startIcon={deleteOverride.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('publicationCatalogTypesPanel.profileCard.resetToDefault')}
          </Button>
        </DialogActions>
      </Dialog>

      {!canManage && selectedType ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('publicationCatalogTypesPanel.viewOnlyNotice')}
        </Alert>
      ) : null}

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
